/**
 * Autosave.
 *
 * Two things it must not do: write on every coalesced Pencil point, and claim to
 * have saved before the transaction commits. The first would put IndexedDB in the
 * ink path; the second is the kind of lie that loses a learner's work and their
 * trust at the same time.
 */

export type SaveStatus = 'idle' | 'pending' | 'saving' | 'saved' | 'error';

export interface AutosaveOptions {
  save: () => Promise<void>;
  /** The brief's window. Long enough to batch a phrase of writing, short enough to feel safe. */
  delayMs?: number;
  onStatus?: (status: SaveStatus, error?: Error) => void;
  /** Injectable for tests. */
  setTimeoutFn?: typeof setTimeout;
  clearTimeoutFn?: typeof clearTimeout;
}

export interface Autosave {
  /** Note that something changed. Resets the debounce. */
  schedule(): void;
  /** Write now — used on page hide, navigation, and before opening another document. */
  flush(): Promise<void>;
  status(): SaveStatus;
  dispose(): void;
}

export function createAutosave(options: AutosaveOptions): Autosave {
  const delay = options.delayMs ?? 500;
  const setT = options.setTimeoutFn ?? setTimeout;
  const clearT = options.clearTimeoutFn ?? clearTimeout;

  let timer: ReturnType<typeof setTimeout> | null = null;
  let status: SaveStatus = 'idle';
  let inFlight: Promise<void> | null = null;
  /** A change that arrived while a save was running still needs writing. */
  let dirtyAgain = false;
  let disposed = false;

  const setStatus = (next: SaveStatus, error?: Error) => {
    status = next;
    options.onStatus?.(next, error);
  };

  async function run(): Promise<void> {
    if (inFlight) {
      dirtyAgain = true;
      return inFlight;
    }
    setStatus('saving');
    inFlight = (async () => {
      try {
        await options.save();
        setStatus('saved');
      } catch (error) {
        setStatus('error', error as Error);
      } finally {
        inFlight = null;
      }
    })();
    await inFlight;

    if (dirtyAgain && !disposed) {
      dirtyAgain = false;
      await run();
    }
  }

  return {
    schedule() {
      if (disposed) return;
      if (timer) clearT(timer);
      setStatus('pending');
      timer = setT(() => {
        timer = null;
        void run();
      }, delay);
    },
    async flush() {
      if (disposed) return;
      if (timer) {
        clearT(timer);
        timer = null;
      }
      // Nothing outstanding: do not write, and do not claim to have.
      if (status === 'idle' || status === 'saved') return;
      await run();
    },
    status: () => status,
    dispose() {
      disposed = true;
      if (timer) clearT(timer);
      timer = null;
    },
  };
}

/**
 * Flush when the page is being hidden or torn down.
 *
 * `pagehide` and `visibilitychange` are the only events iOS Safari reliably
 * delivers when an app is swiped away; `beforeunload` is not one of them.
 */
export function flushOnHide(flush: () => void): () => void {
  const onHide = () => flush();
  const onVisibility = () => {
    if (document.visibilityState === 'hidden') flush();
  };
  window.addEventListener('pagehide', onHide);
  document.addEventListener('visibilitychange', onVisibility);
  return () => {
    window.removeEventListener('pagehide', onHide);
    document.removeEventListener('visibilitychange', onVisibility);
  };
}
